import {Component, ElementRef, Injector, OnInit, ViewChild} from '@angular/core';
import {CompanyDocument} from "../../models/CompanyDocument";
import {CompanyDocumentService} from "../../services/companyDocument/document.service";
import {ActivatedRoute} from "@angular/router";
import * as moment from 'moment';
import {AthleteService} from "../../services/athlete/athlete.service";
import {BaseComponent} from "../base/base.component";
import Swal from "sweetalert2";
import {todayIsoDate} from "../../resources/person-validation";

@Component({
    selector: 'app-athlete-documents',
    templateUrl: './athlete-documents.component.html'
})
export class AthleteDocumentsComponent extends BaseComponent implements OnInit {

    @ViewChild('frontInput') frontInput?: ElementRef<HTMLInputElement>;
    @ViewChild('backInput') backInput?: ElementRef<HTMLInputElement>;

    athleteCompanyDocuments: CompanyDocument[] = [];
    companyDocuments: CompanyDocument[] = [];
    missingCompanyDocuments: CompanyDocument[] = [];
    athleteId: string = '';

    typeOfDocument: string = '';
    file: File | null = null;
    file2: File | null = null;
    expirationDate?: string;

    loading = true;
    uploading = false;
    uploadSubmitted = false;
    readonly today = todayIsoDate();

    /** Documento caricato in modifica (solo data di scadenza). */
    editingDocumentId: string | null = null;
    editExpirationDate = '';
    savingEdit = false;

    private readonly companyId = localStorage.getItem('wisper_companyId') ?? '';

    constructor(public athleteService: AthleteService, public companyDocumentService: CompanyDocumentService, public route: ActivatedRoute, public serviceInjector: Injector) {
        super(serviceInjector)
    }

    async ngOnInit() {
        if (this.route.snapshot.params['athleteId']) this.athleteId = this.route.snapshot.params['athleteId'];
        await this.loadDocuments();
    }

    get selectedDocument(): CompanyDocument | undefined {
        return this.companyDocuments.find(doc => doc.id === this.typeOfDocument);
    }

    get uploadErrors(): { type?: string; file?: string; expiration?: string } {
        const errors: { type?: string; file?: string; expiration?: string } = {};
        if (!this.typeOfDocument) errors.type = 'Seleziona il tipo di documento.';
        if (!this.file) errors.file = 'Seleziona il file da caricare (fronte).';
        if (this.selectedDocument?.hasExpirationDate) {
            if (!this.expirationDate) errors.expiration = 'Inserisci la data di scadenza.';
            else if (this.expirationDate < this.today) errors.expiration = 'Il documento risulta scaduto: carica un documento in corso di validità.';
        }
        return errors;
    }

    get hasUploadErrors(): boolean {
        return Object.keys(this.uploadErrors).length > 0;
    }

    onDocumentTypeChange(): void {
        this.expirationDate = undefined;
    }

    chooseDocument(event: any) {
        this.file = event.target.files?.[0] ?? null;
        if (!this.file) this.file2 = null;
    }

    chooseDocument2(event: any) {
        this.file2 = event.target.files?.[0] ?? null;
    }

    clearFile(which: 'file' | 'file2', input: HTMLInputElement) {
        input.value = '';
        this[which] = null;
        if (which === 'file') this.file2 = null;
    }

    fileSize(file: File): string {
        return file.size >= 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`;
    }

    uploadTimeLabel(document: CompanyDocument): string {
        const updatedAt = document.AthleteCompanyDocument?.updatedAt;
        if (!updatedAt) return '';
        const passedDays = moment().startOf('day').diff(moment(updatedAt).startOf('day'), 'days');
        if (passedDays > 1) return `Caricato ${passedDays} giorni fa`;
        if (passedDays === 1) return 'Caricato ieri';
        return 'Caricato oggi';
    }

    expirationLabel(document: CompanyDocument): { text: string; classes: string } | null {
        const expiration = document.AthleteCompanyDocument?.expirationDate;
        if (!expiration) return null;
        const date = moment(expiration);
        const days = date.startOf('day').diff(moment().startOf('day'), 'days');
        const formatted = date.format('DD/MM/YYYY');
        if (days < 0) return {text: `Scaduto il ${formatted}`, classes: 'bg-red-50 text-red-700'};
        if (days <= 30) return {text: `Scade il ${formatted}`, classes: 'bg-amber-50 text-amber-800'};
        return {text: `Valido fino al ${formatted}`, classes: 'bg-slate-100 text-slate-600'};
    }

    downloadDocument(url?: string) {
        if (url) window.open(url, '_blank');
    }

    async uploadDocument() {
        this.uploadSubmitted = true;
        if (this.hasUploadErrors || !this.file || this.uploading) return;

        const formData = new FormData();
        formData.append('file', this.file);
        if (this.file2) formData.append('file2', this.file2);

        this.uploading = true;
        try {
            const expiration = this.selectedDocument?.hasExpirationDate ? this.expirationDate : undefined;
            if (await this.companyDocumentService.uploadCompanyDocument(formData, this.typeOfDocument, expiration, this.athleteId)) {
                Swal.fire({icon: 'success', title: 'Documento caricato con successo'});
                this.resetUploadForm();
                await this.loadDocuments();
            } else {
                this.showErrorMessage('Errore nel caricamento del documento, riprova.');
            }
        } finally {
            this.uploading = false;
        }
    }

    async deleteAthleteDocument(documentId?: string) {
        const result = await Swal.fire({
            icon: 'warning',
            title: "Sei sicuro di voler eliminare il documento selezionato?",
            showCancelButton: true,
            confirmButtonText: "Sì, elimina il documento",
            cancelButtonText: "No, annulla l'operazione"
        });

        if (result.isConfirmed) {
            const response = await this.athleteService.deleteAthleteDocuments(this.athleteId, documentId ?? '');
            if (response.success) {
                Swal.fire({icon: 'success', title: 'Documento eliminato con successo'});
                await this.loadDocuments();
            } else {
                this.showErrorMessage('Errore nell\'eliminazione del documento, riprova.');
            }
        }
    }

    startEdit(document: CompanyDocument) {
        this.editingDocumentId = document.id ?? null;
        const expiration = document.AthleteCompanyDocument?.expirationDate;
        this.editExpirationDate = expiration ? moment(expiration).format('YYYY-MM-DD') : '';
    }

    cancelEdit() {
        this.editingDocumentId = null;
    }

    editExpirationError(): string | null {
        if (!this.editExpirationDate) return 'Inserisci la data di scadenza.';
        if (this.editExpirationDate < this.today) return 'La data di scadenza non può essere nel passato.';
        return null;
    }

    async saveEditedDocument(document: CompanyDocument) {
        const athleteDocumentId = (document.AthleteCompanyDocument as any)?.id;
        if (this.editExpirationError() || !athleteDocumentId) return;

        this.savingEdit = true;
        try {
            if (await this.companyDocumentService.updateAthleteCompanyDocument(athleteDocumentId, this.editExpirationDate)) {
                this.editingDocumentId = null;
                await this.loadDocuments();
            } else {
                this.showErrorMessage('Errore nell\'aggiornamento del documento, riprova.');
            }
        } finally {
            this.savingEdit = false;
        }
    }

    private async loadDocuments() {
        try {
            const [athleteDocuments, missingDocuments, companyDocuments] = await Promise.all([
                this.athleteService.getAthleteDocuments(this.athleteId),
                this.athleteService.checkAthleteMandatoryDocuments(this.athleteId, this.companyId),
                this.athleteId ? this.companyDocumentService.getCompanyDocuments(this.companyId, this.athleteId) : Promise.resolve([]),
            ]);
            this.athleteCompanyDocuments = (athleteDocuments as any)?.data ?? [];
            this.missingCompanyDocuments = missingDocuments ?? [];
            this.companyDocuments = companyDocuments ?? [];
        } catch (e) {
            console.error('error loading athlete documents: ', e);
        } finally {
            this.loading = false;
        }
    }

    private resetUploadForm() {
        this.typeOfDocument = '';
        this.file = null;
        this.file2 = null;
        this.expirationDate = undefined;
        this.uploadSubmitted = false;
        if (this.frontInput) this.frontInput.nativeElement.value = '';
        if (this.backInput) this.backInput.nativeElement.value = '';
    }
}
